// @ts-check
/** @typedef {import('../types/api').Order} Order */

import { Eye, RefreshCw, Search } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import CreateOrderModal from '../components/CreateOrderModal';
import LoadingScreen from '../components/LoadingScreen';
import OrderDetailModal from '../components/OrderDetailModal';
import PosSummaryStrip from '../components/PosSummaryStrip';
import BulkActionBar from '../components/ui/BulkActionBar';
import BulkConfirmModal from '../components/ui/BulkConfirmModal';
import { HeaderCheckbox, RowCheckbox } from '../components/ui/RowCheckbox';
import { useToast } from '../context/ToastContext';
import useRowSelection from '../hooks/useRowSelection';
import adminOrderService from '../services/adminOrderService';
import { reportBulkResult, runBulk } from '../services/bulkService';
import { downloadCsv } from '../utils/csv';
import { isDispatchOverdue } from '../utils/dispatch';
import { formatDay, todayInIndia } from '../utils/salesEvents';

// Orders only move forward in bulk; cancelling is a one-at-a-time action (stock + refund).
const BULK_STATUSES = [
  ['confirmed', 'Confirmed'],
  ['processing', 'Processing'],
  ['shipped', 'Shipped'],
  ['delivered', 'Delivered'],
];

const OrdersPage = () => {
  const toast = useToast();
  /** @type {[Order[], Function]} */
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [orderTypeFilter, setOrderTypeFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [channelFilter, setChannelFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [posDay, setPosDay] = useState(todayInIndia());
  const [posEventId, setPosEventId] = useState('');
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('confirmed');
  const [bulkNotify, setBulkNotify] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);
  const pageLimit = 50;

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminOrderService.getAllOrders({
        page: currentPage,
        limit: pageLimit,
        search: search || undefined,
        order_type: orderTypeFilter || undefined,
        sales_channel: channelFilter || undefined,
        payment_status: paymentStatusFilter || undefined,
      });

      // Handle paginated response
      if (data.items) {
        setOrders(data.items);
        setTotalPages(data.pages || 1);
        setTotalOrders(data.total || 0);
      } else {
        // Fallback for old format
        const arr = /** @type {Order[]} */ (/** @type {unknown} */ (data));
        setOrders(arr);
        setTotalOrders(arr.length);
      }
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [search, currentPage, pageLimit, orderTypeFilter, channelFilter, paymentStatusFilter, toast]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setCurrentPage(1); // Reset to page 1 on search
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300',
      confirmed: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
      processing: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
      shipped: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
      delivered: 'bg-green-200 dark:bg-green-900/60 text-green-900 dark:text-green-300',
      cancelled: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
    };
    return colors[status] || 'bg-muted text-foreground';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300',
      partially_paid: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
      paid: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
      failed: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
      refunded: 'bg-muted text-foreground',
    };
    return colors[status] || 'bg-muted text-foreground';
  };

  const selection = useRowSelection(
    orders.map((o) => o.id),
    { resetKey: `${search}|${orderTypeFilter}|${channelFilter}|${paymentStatusFilter}|${currentPage}` },
  );

  const handleBulkStatus = async () => {
    setBulkBusy(true);
    try {
      const result = await runBulk('/api/admin/orders/bulk', {
        ids: selection.selectedIds,
        action: 'set_status',
        params: { status: bulkStatus, notify: bulkNotify },
      });
      reportBulkResult(toast, result, 'updated', 'order');
      setBulkStatusOpen(false);
      selection.clear();
      loadOrders();
    } catch (error) {
      toast.error(error.message || 'Failed to update orders');
    } finally {
      setBulkBusy(false);
    }
  };

  const exportSelected = () => {
    const chosen = orders.filter((o) => selection.isSelected(o.id));
    downloadCsv('orders.csv', [
      ['Order #', 'Date', 'Customer', 'Email', 'Total', 'Order status', 'Payment status', 'Channel'],
      ...chosen.map((o) => [
        o.order_number,
        new Date(o.created_at).toLocaleDateString(),
        [o.shipping_first_name, o.shipping_last_name].filter(Boolean).join(' '),
        o.customer_email && o.customer_email !== 'N/A' ? o.customer_email : o.recipient_email,
        o.total,
        o.order_status,
        o.payment_status,
        o.sales_channel,
      ]),
    ]);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-serif">Orders ({totalOrders})</h1>
        <button
          onClick={loadOrders}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg border-input hover:bg-muted/50 disabled:opacity-50"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70" size={20} />
          <input
            type="text"
            placeholder="Search by order number..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
          />
        </div>
        <select
          value={orderTypeFilter}
          onChange={(e) => setOrderTypeFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-input rounded-lg"
        >
          <option value="">All order types</option>
          <option value="standard">Standard</option>
          <option value="gift">Gift</option>
          <option value="sample">Sample</option>
        </select>
        <select
          value={channelFilter}
          onChange={(e) => {
            setChannelFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="px-3 py-2 text-sm border border-input rounded-lg"
        >
          <option value="">All channels</option>
          <option value="online">Online</option>
          <option value="pop_up">Pop-up</option>
        </select>
        <select
          value={paymentStatusFilter}
          onChange={(e) => {
            setPaymentStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="px-3 py-2 text-sm border border-input rounded-lg"
        >
          <option value="">All payment statuses</option>
          <option value="partially_paid">Partially paid</option>
          <option value="paid">Paid</option>
          <option value="awaiting_payment">Awaiting payment</option>
        </select>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
        >
          Create Order
        </button>
      </div>

      {channelFilter === 'pop_up' && (
        <PosSummaryStrip day={posDay} onDayChange={setPosDay} eventId={posEventId} onEventChange={setPosEventId} />
      )}

      {(orderTypeFilter === 'gift' || orderTypeFilter === 'sample') && (
        <p className="text-sm text-muted-foreground mb-2">
          Total value {orderTypeFilter === 'gift' ? 'gifted' : 'sent as samples'} (this page): ₹
          {orders.reduce((sum, o) => sum + (o.subtotal || 0), 0).toLocaleString()}
        </p>
      )}

      <BulkActionBar count={selection.count} onClear={selection.clear}>
        <BulkActionBar.Button onClick={() => setBulkStatusOpen(true)}>Update status</BulkActionBar.Button>
        <BulkActionBar.Button onClick={exportSelected}>Export CSV</BulkActionBar.Button>
      </BulkActionBar>

      <div className="bg-card border rounded-lg border-border shadow-sm overflow-x-auto">
        <table className="w-full min-w-[960px]">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="pl-6 py-3">
                <HeaderCheckbox selection={selection} />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Order #</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Customer</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Total</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Dispatch by</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">
                Order Status
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Payment</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {orders.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center text-muted-foreground">
                  No orders found
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order.id} className="hover:bg-muted/50">
                  <td className="pl-6 py-4">
                    <RowCheckbox selection={selection} id={order.id} label={`Select order ${order.order_number}`} />
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-foreground">
                    {order.order_number}
                    {order.sales_channel === 'pop_up' && (
                      <span
                        className="ml-2 inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-300"
                        title={order.event_name || 'Pop-up'}
                      >
                        Pop-up
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {new Date(order.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground">
                    <div>
                      {[order.shipping_first_name, order.shipping_last_name].filter(Boolean).join(' ') || 'N/A'}
                    </div>
                    <div className="text-xs text-muted-foreground/70">
                      {order.customer_email && order.customer_email !== 'N/A'
                        ? order.customer_email
                        : order.recipient_email || ''}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-right font-semibold">
                    {'\u20B9'}
                    {parseFloat(String(order.total)).toLocaleString('en-IN')}
                  </td>
                  <td className="px-6 py-4 text-center text-sm">
                    {order.promised_dispatch_date ? (
                      <span
                        className={
                          isDispatchOverdue(order.promised_dispatch_date, order.order_status, todayInIndia())
                            ? 'font-semibold text-red-600 dark:text-red-400'
                            : 'text-foreground/80'
                        }
                      >
                        {formatDay(order.promised_dispatch_date)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/50">&mdash;</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span
                      className={`inline-flex px-3 py-1 rounded-md text-xs font-medium ${getStatusColor(order.order_status)}`}
                    >
                      {order.order_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span
                      className={`inline-flex px-3 py-1 rounded-md text-xs font-medium ${getPaymentStatusColor(order.payment_status)}`}
                    >
                      {order.payment_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => {
                        setSelectedOrderId(order.id);
                        setShowDetailModal(true);
                      }}
                      className="text-muted-foreground hover:text-foreground"
                      title="View Details"
                    >
                      <Eye size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-input rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/50"
          >
            Previous
          </button>

          <span className="px-4 py-2 text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-input rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/50"
          >
            Next
          </button>
        </div>
      )}

      {/* Order Detail Modal */}
      {showDetailModal && selectedOrderId && (
        <OrderDetailModal
          orderId={selectedOrderId}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedOrderId(null);
          }}
          onUpdate={() => {
            loadOrders();
          }}
        />
      )}

      {showCreateModal && (
        <CreateOrderModal
          onClose={() => {
            setShowCreateModal(false);
            // Refresh only once the modal is closed: the page swaps to a loader while it reloads,
            // which would tear the modal down mid-payment.
            loadOrders();
          }}
          onOrderCreated={(order) => {
            setShowCreateModal(false);
            loadOrders();
            // Jump straight to the new order's detail so the admin can add a shipment
            // without hunting for it in the list.
            setSelectedOrderId(order.id);
            setShowDetailModal(true);
          }}
        />
      )}

      {bulkStatusOpen && (
        <BulkConfirmModal
          title="Update order status"
          message="Orders can only move forward. Orders that are cancelled, already at this status, or further along are skipped and listed afterwards. To cancel an order, open it."
          count={selection.count}
          confirmLabel="Update"
          busy={bulkBusy}
          onConfirm={handleBulkStatus}
          onClose={() => setBulkStatusOpen(false)}
        >
          <select
            aria-label="New status"
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          >
            {BULK_STATUSES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-foreground/80">
            <input type="checkbox" checked={bulkNotify} onChange={(e) => setBulkNotify(e.target.checked)} />
            Notify customers by email
          </label>
        </BulkConfirmModal>
      )}
    </div>
  );
};

export default OrdersPage;
